import React, { useEffect, useState, useCallback } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { apiError } from "../../api";
import { useLang } from "../../i18n";
import { useConfig } from "../../context/ConfigContext";
import MagicalCard from "../../components/MagicalCard";
import Avatar from "../../components/Avatar";
import { StatCard, Icon, Modal, EmptyState, SkeletonPanel } from "../../components/common";
import { fmtRelative } from "../../utils";
import { toast } from "sonner";

const TABS = ["overview", "teachers", "levels", "categories", "badges", "users", "settings", "audit"];
const CHART_COLORS = ["#9C42F5", "#EF4D9A", "#27C9EE", "#FFC83D", "#48D276", "#268CEB"];

function Overview() {
  const { t, pick } = useLang();
  const { config } = useConfig();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <SkeletonPanel lines={6} />;
  const cats = config?.categories || [];
  const pie = stats.top_categories.map((c, i) => ({
    name: c.key === "deduction" ? t("teacher.deduct_points") : pick(cats.find((x) => x.key === c.key) || { name_ar: c.key, name_en: c.key }, "name"),
    value: c.count, color: CHART_COLORS[i % CHART_COLORS.length],
  }));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="admin-stat-students" icon="Users" label={t("admin.students")} value={stats.students} />
        <StatCard testId="admin-stat-teachers" icon="GraduationCap" label={t("admin.teachers")} value={stats.teachers} gradient="from-[#268CEB] to-[#27C9EE]" />
        <StatCard testId="admin-stat-avg" icon="Star" label={t("admin.avg_points")} value={stats.average_points} gradient="from-[#FFAE32] to-[#FFC83D]" />
        <StatCard testId="admin-stat-tx" icon="History" label={t("admin.transactions")} value={stats.transactions} gradient="from-[#EF4D9A] to-[#FF63B5]" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <MagicalCard title={t("admin.weekly_activity")} variant="ribbon-cyan" testId="admin-weekly-chart">
          <div dir="ltr" className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.weekly_activity} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fill: "#A5A0D6", fontSize: 10 }} axisLine={false} tickLine={false}
                  tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#A5A0D6", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#171347", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, color: "#fff" }} />
                <Bar dataKey="points" radius={[8, 8, 2, 2]} fill="#9C42F5" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </MagicalCard>
        <MagicalCard title={t("admin.top_categories")} variant="ribbon-pink" testId="admin-categories-chart">
          <div dir="ltr" className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={4}>
                  {pie.map((p, i) => <Cell key={i} fill={p.color} stroke="transparent" />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#171347", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            {pie.map((p) => (
              <span key={p.name} className="text-[11px] font-bold flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} /> {p.name}
              </span>
            ))}
          </div>
        </MagicalCard>
      </div>
    </div>
  );
}

function LevelsTab() {
  const { t, pick } = useLang();
  const { config, reload } = useConfig();
  const [levels, setLevels] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/levels").then((r) => setLevels(r.data.levels)), []);
  useEffect(() => { load(); }, [load]);

  const openForm = (lv) => {
    setEditing(lv);
    setForm(lv ? { ...lv } : { key: "", order: (levels?.length || 0) + 1, name_ar: "", name_en: "", min_points: 0, icon: "/assets/levels/star.jpeg", color: "#9C42F5" });
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/levels/${editing.id}`, form);
      else await api.post("/admin/levels", form);
      toast.success(t("common.saved_ok"));
      setForm(null); load(); reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };
  const remove = async (lv) => {
    if (!window.confirm(t("admin.delete_confirm"))) return;
    try {
      await api.delete(`/admin/levels/${lv.id}`);
      toast.success(t("common.saved_ok")); load(); reload();
    } catch (e) { toast.error(apiError(e, t)); }
  };

  if (!levels) return <SkeletonPanel lines={4} />;
  return (
    <div className="space-y-4">
      <button data-testid="add-level-btn" onClick={() => openForm(null)} className="btn-magic text-sm"><Plus className="w-4 h-4" /> {t("admin.add_level")}</button>
      <div className="grid gap-3">
        {levels.map((lv) => (
          <div key={lv.id} data-testid={`admin-level-${lv.key}`} className="magic-panel p-4 flex items-center gap-4 flex-wrap">
            <img src={lv.icon} alt="" className="w-12 h-12 rounded-full object-cover ring-2" style={{ ringColor: lv.color }} />
            <div className="flex-1 min-w-[140px]">
              <p className="font-extrabold">{pick(lv, "name")}</p>
              <p className="text-xs text-[#A5A0D6] font-bold">{lv.name_ar} — {lv.name_en}</p>
            </div>
            <span className="text-sm font-black" style={{ color: lv.color }}>{t("admin.min_points")}: {lv.min_points}</span>
            <button data-testid={`edit-level-${lv.key}`} onClick={() => openForm(lv)} className="w-9 h-9 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`delete-level-${lv.key}`} onClick={() => remove(lv)} className="w-9 h-9 rounded-xl bg-[#EF4D9A]/15 text-[#FF8FB1] hover:bg-[#EF4D9A]/30 flex items-center justify-center transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Modal open={!!form} onClose={() => setForm(null)} title={editing ? t("common.edit") : t("admin.add_level")} testId="level-form-modal">
        {form && (
          <div className="grid grid-cols-2 gap-3">
            {!editing && (
              <div className="col-span-2">
                <label className="text-sm font-extrabold block mb-1">{t("admin.key")} *</label>
                <input data-testid="level-key" className="input-magic" dir="ltr" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} />
              </div>
            )}
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_ar")}</label>
              <input data-testid="level-name-ar" className="input-magic" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_en")}</label>
              <input data-testid="level-name-en" className="input-magic" dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.min_points")}</label>
              <input data-testid="level-min-points" type="number" min="0" className="input-magic" value={form.min_points} onChange={(e) => setForm({ ...form, min_points: Number(e.target.value) })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.color")}</label>
              <input data-testid="level-color" type="color" className="input-magic h-11" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
            <button data-testid="level-save-btn" disabled={busy} onClick={save} className="btn-magic col-span-2">{busy ? t("common.loading") : t("common.save")}</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

function CategoriesTab() {
  const { t, pick } = useLang();
  const { reload } = useConfig();
  const [cats, setCats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/categories").then((r) => setCats(r.data.categories)), []);
  useEffect(() => { load(); }, [load]);

  const openForm = (c) => {
    setEditing(c);
    setForm(c ? { ...c } : { key: "", name_ar: "", name_en: "", default_points: 5, icon: "Star", color: "#9C42F5", active: true, order: (cats?.length || 0) + 1 });
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, form);
      else await api.post("/admin/categories", form);
      toast.success(t("common.saved_ok"));
      setForm(null); load(); reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  if (!cats) return <SkeletonPanel lines={4} />;
  return (
    <div className="space-y-4">
      <button data-testid="add-category-btn" onClick={() => openForm(null)} className="btn-magic text-sm"><Plus className="w-4 h-4" /> {t("admin.add_category")}</button>
      <div className="grid gap-3">
        {cats.map((c) => (
          <div key={c.id} data-testid={`admin-category-${c.key}`} className="magic-panel p-4 flex items-center gap-4 flex-wrap">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${c.color}2B`, boxShadow: `0 0 10px ${c.color}44` }}>
              <Icon name={c.icon} className="w-5 h-5" style={{ color: c.color }} />
            </span>
            <div className="flex-1 min-w-[140px]">
              <p className="font-extrabold">{pick(c, "name")}</p>
              <p className="text-xs text-[#A5A0D6] font-bold">{c.name_ar} — {c.name_en}</p>
            </div>
            <span className="text-lg font-black" style={{ color: c.color }}>+{c.default_points}</span>
            <span className={`text-[11px] font-extrabold px-2.5 py-1 rounded-full border ${c.active ? "text-[#48D276] border-[#48D276]/40 bg-[#48D276]/10" : "text-[#A5A0D6] border-white/15"}`}>
              {c.active ? t("admin.active") : t("admin.disabled")}
            </span>
            <button data-testid={`edit-category-${c.key}`} onClick={() => openForm(c)} className="w-9 h-9 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Pencil className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Modal open={!!form} onClose={() => setForm(null)} title={editing ? t("common.edit") : t("admin.add_category")} testId="category-form-modal">
        {form && (
          <div className="grid grid-cols-2 gap-3">
            {!editing && (
              <div className="col-span-2"><label className="text-sm font-extrabold block mb-1">{t("admin.key")} *</label>
                <input data-testid="category-key" className="input-magic" dir="ltr" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} /></div>
            )}
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_ar")}</label>
              <input data-testid="category-name-ar" className="input-magic" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_en")}</label>
              <input data-testid="category-name-en" className="input-magic" dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.default_points")}</label>
              <input data-testid="category-points" type="number" min="1" max="100" className="input-magic" value={form.default_points} onChange={(e) => setForm({ ...form, default_points: Number(e.target.value) })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.color")}</label>
              <input data-testid="category-color" type="color" className="input-magic h-11" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
            <label className="col-span-2 flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/10 px-4 py-3 cursor-pointer">
              <input data-testid="category-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-5 h-5 accent-[#9C42F5]" />
              <span className="text-sm font-bold">{t("admin.active")}</span>
            </label>
            <button data-testid="category-save-btn" disabled={busy} onClick={save} className="btn-magic col-span-2">{busy ? t("common.loading") : t("common.save")}</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

function BadgesTab() {
  const { t, pick } = useLang();
  const { config, reload } = useConfig();
  const [badges, setBadges] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const cats = config?.categories || [];

  const load = useCallback(() => api.get("/admin/badges").then((r) => setBadges(r.data.badges)), []);
  useEffect(() => { load(); }, [load]);

  const openForm = (b) => {
    setEditing(b);
    setForm(b ? { ...b, auto_category: b.auto?.category_key || "", auto_count: b.auto?.count || "" }
      : { key: "", name_ar: "", name_en: "", desc_ar: "", desc_en: "", icon: "Award", color: "#FFC83D", auto_category: "", auto_count: "", active: true, order: (badges?.length || 0) + 1 });
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, auto_category: form.auto_category || null, auto_count: form.auto_count ? Number(form.auto_count) : null };
      if (editing) await api.put(`/admin/badges/${editing.id}`, payload);
      else await api.post("/admin/badges", payload);
      toast.success(t("common.saved_ok"));
      setForm(null); load(); reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  if (!badges) return <SkeletonPanel lines={4} />;
  return (
    <div className="space-y-4">
      <button data-testid="add-badge-btn" onClick={() => openForm(null)} className="btn-magic text-sm"><Plus className="w-4 h-4" /> {t("admin.add_badge")}</button>
      <div className="grid gap-3">
        {badges.map((b) => (
          <div key={b.id} data-testid={`admin-badge-${b.key}`} className="magic-panel p-4 flex items-center gap-4 flex-wrap">
            <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${b.color}, ${b.color}99)`, boxShadow: `0 0 12px ${b.color}55` }}>
              <Icon name={b.icon} className="w-5 h-5 text-white" />
            </span>
            <div className="flex-1 min-w-[140px]">
              <p className="font-extrabold">{pick(b, "name")}</p>
              <p className="text-xs text-[#A5A0D6] font-bold">
                {b.auto ? `${t("admin.auto_rule")}: ${b.auto.count} × ${b.auto.category_key}` : t("admin.manual_badge")}
              </p>
            </div>
            <button data-testid={`edit-badge-${b.key}`} onClick={() => openForm(b)} className="w-9 h-9 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Pencil className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Modal open={!!form} onClose={() => setForm(null)} title={editing ? t("common.edit") : t("admin.add_badge")} testId="badge-form-modal" wide>
        {form && (
          <div className="grid grid-cols-2 gap-3">
            {!editing && (
              <div className="col-span-2"><label className="text-sm font-extrabold block mb-1">{t("admin.key")} *</label>
                <input data-testid="badge-key" className="input-magic" dir="ltr" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} /></div>
            )}
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_ar")}</label>
              <input data-testid="badge-name-ar" className="input-magic" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_en")}</label>
              <input data-testid="badge-name-en" className="input-magic" dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.desc_ar")}</label>
              <input data-testid="badge-desc-ar" className="input-magic" dir="rtl" value={form.desc_ar} onChange={(e) => setForm({ ...form, desc_ar: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.desc_en")}</label>
              <input data-testid="badge-desc-en" className="input-magic" dir="ltr" value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.color")}</label>
              <input data-testid="badge-color" type="color" className="input-magic h-11" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
            <div><label className="text-sm font-extrabold block mb-1">{t("admin.auto_rule")}</label>
              <div className="flex gap-2">
                <select data-testid="badge-auto-category" className="input-magic" value={form.auto_category} onChange={(e) => setForm({ ...form, auto_category: e.target.value })}>
                  <option value="" label={t("admin.manual_badge")} />
                  {cats.map((c) => <option key={c.key} value={c.key} label={pick(c, "name")} />)}
                </select>
                <input data-testid="badge-auto-count" type="number" min="1" className="input-magic !w-20" value={form.auto_count} onChange={(e) => setForm({ ...form, auto_count: e.target.value })} />
              </div>
            </div>
            <button data-testid="badge-save-btn" disabled={busy} onClick={save} className="btn-magic col-span-2">{busy ? t("common.loading") : t("common.save")}</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

function UsersTab() {
  const { t, pick } = useLang();
  const { config } = useConfig();
  const [users, setUsers] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ role: "student", name_ar: "", name_en: "", username: "", class_id: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);

  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data.users)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", { ...form, class_id: form.class_id || null, password: form.password || null });
      toast.success(t("common.saved_ok"));
      setTempPw(data.temp_password);
      setFormOpen(false);
      setForm({ role: "student", name_ar: "", name_en: "", username: "", class_id: "", password: "" });
      load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };
  const toggleActive = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
    toast.success(t("common.saved_ok"));
    load();
  };
  const resetPw = async (u) => {
    const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
    setTempPw(data.temp_password);
  };

  if (!users) return <SkeletonPanel lines={4} />;
  return (
    <div className="space-y-4">
      <button data-testid="add-user-btn" onClick={() => setFormOpen(true)} className="btn-magic text-sm"><Plus className="w-4 h-4" /> {t("admin.add_user")}</button>
      {tempPw && (
        <div data-testid="temp-password-banner" className="rounded-2xl bg-[#FFC83D]/12 border border-[#FFC83D]/40 p-4 text-center" dir="ltr">
          <p className="text-xs font-bold text-[#A5A0D6]">{t("teacher.temp_password")}</p>
          <p className="text-2xl font-black text-[#FFC83D]">{tempPw}</p>
        </div>
      )}
      <div className="grid gap-3">
        {users.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.username}`} className={`magic-panel p-4 flex items-center gap-4 flex-wrap ${!u.is_active ? "opacity-55" : ""}`}>
            <Avatar src={u.avatar} name={pick(u, "name")} size="sm" />
            <div className="flex-1 min-w-[140px]">
              <p className="font-extrabold">{pick(u, "name")}</p>
              <p className="text-xs text-[#A5A0D6] font-bold" dir="ltr">@{u.username}</p>
            </div>
            <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-[#7625D8]/20 border border-[#9C42F5]/40">{t(`auth.demo_${u.role === "admin" ? "admin" : u.role}`)}</span>
            <button data-testid={`reset-pw-${u.username}`} onClick={() => resetPw(u)} className="text-xs font-extrabold text-[#27C9EE] hover:text-white transition-colors">{t("admin.reset_password")}</button>
            <button data-testid={`toggle-user-${u.username}`} onClick={() => toggleActive(u)}
              className={`text-xs font-extrabold px-3 py-1.5 rounded-full border transition-colors ${u.is_active ? "text-[#FF8FB1] border-[#EF4D9A]/40 hover:bg-[#EF4D9A]/15" : "text-[#48D276] border-[#48D276]/40 hover:bg-[#48D276]/15"}`}>
              {u.is_active ? t("admin.disable") : t("admin.enable")}
            </button>
          </div>
        ))}
      </div>
      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={t("admin.add_user")} testId="user-form-modal">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-sm font-extrabold block mb-1">{t("admin.role")}</label>
            <select data-testid="user-role" className="input-magic" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="student" label={t("auth.demo_student")} />
              <option value="teacher" label={t("auth.demo_teacher")} />
            </select>
          </div>
          <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_ar")}</label>
            <input data-testid="user-name-ar" className="input-magic" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></div>
          <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_en")}</label>
            <input data-testid="user-name-en" className="input-magic" dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></div>
          <div><label className="text-sm font-extrabold block mb-1">{t("teacher.username")}</label>
            <input data-testid="user-username" className="input-magic" dir="ltr" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
          <div><label className="text-sm font-extrabold block mb-1">{t("teacher.class_label")}</label>
            <select data-testid="user-class" className="input-magic" value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })}>
              <option value="" label="—" />
              {(config?.classes || []).map((c) => <option key={c.id} value={c.id} label={c.name_ar} />)}
            </select></div>
          <div className="col-span-2"><label className="text-sm font-extrabold block mb-1">{t("teacher.password_optional")}</label>
            <input data-testid="user-password" className="input-magic" dir="ltr" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <button data-testid="user-save-btn" disabled={busy} onClick={create} className="btn-magic col-span-2">{busy ? t("common.loading") : t("common.save")}</button>
        </div>
      </Modal>
    </div>
  );
}

function SettingsTab() {
  const { t } = useLang();
  const { reload } = useConfig();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data.settings)); }, []);
  if (!settings) return <SkeletonPanel lines={5} />;
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success(t("common.saved_ok"));
      reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  const textField = (key, dir = "rtl") => (
    <div>
      <label className="text-sm font-extrabold block mb-1">{t(`admin.${key}`)}</label>
      <input data-testid={`setting-${key}`} className="input-magic" dir={dir} value={settings[key] || ""} onChange={(e) => set(key, e.target.value)} />
    </div>
  );
  const toggleField = (key) => (
    <label className="flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/10 px-4 py-3 cursor-pointer">
      <input data-testid={`setting-${key}`} type="checkbox" checked={!!settings[key]} onChange={(e) => set(key, e.target.checked)} className="w-5 h-5 accent-[#9C42F5]" />
      <span className="text-sm font-bold">{t(`admin.${key}`)}</span>
    </label>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="grid sm:grid-cols-2 gap-3">
        {textField("journey_title_ar")}{textField("journey_title_en", "ltr")}
        {textField("school_name_ar")}{textField("school_name_en", "ltr")}
        {textField("term_name_ar")}{textField("term_name_en", "ltr")}
        {textField("term_goal_ar")}{textField("term_goal_en", "ltr")}
        {textField("motivation_ar")}{textField("motivation_en", "ltr")}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("admin.name_display")}</label>
          <select data-testid="setting-name_display" className="input-magic" value={settings.name_display} onChange={(e) => set("name_display", e.target.value)}>
            <option value="full" label={t("admin.name_full")} />
            <option value="first" label={t("admin.name_first")} />
            <option value="abbrev" label={t("admin.name_abbrev")} />
          </select>
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("admin.leaderboard_size")}</label>
          <input data-testid="setting-leaderboard_size" type="number" min="1" max="20" className="input-magic" value={settings.leaderboard_size} onChange={(e) => set("leaderboard_size", Number(e.target.value))} />
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("admin.near_threshold")}</label>
          <input data-testid="setting-near_level_threshold" type="number" min="1" max="100" className="input-magic" value={settings.near_level_threshold} onChange={(e) => set("near_level_threshold", Number(e.target.value))} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {toggleField("leaderboard_visible")}
        {toggleField("negative_points_allowed")}
        {toggleField("teacher_can_adjust_points")}
        {toggleField("teacher_can_deduct")}
      </div>
      <button data-testid="settings-save-btn" disabled={busy} onClick={save} className="btn-magic btn-gold w-full sm:w-auto">
        {busy ? t("common.loading") : t("common.save")}
      </button>
    </div>
  );
}

function TeachersTab() {
  const { t, pick } = useLang();
  const [teachers, setTeachers] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ name_ar: "", name_en: "", username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [showDisabled, setShowDisabled] = useState(false);

  const load = useCallback(() => api.get("/admin/teachers").then((r) => setTeachers(r.data.teachers)), []);
  useEffect(() => { load(); }, [load]);
  const visibleTeachers = (teachers || []).filter((u) => showDisabled || u.is_active);

  const create = async () => {
    if (!form.name_ar.trim() || !form.username.trim()) { toast.error(t("validation.required")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", {
        role: "teacher", name_ar: form.name_ar, name_en: form.name_en,
        username: form.username, class_id: null, password: form.password || null,
      });
      toast.success(t("admin.teacher_created"));
      setTempPw(data.temp_password);
      setFormOpen(false);
      setForm({ name_ar: "", name_en: "", username: "", password: "" });
      load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };
  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(t("common.saved_ok"));
      load();
    } catch (e) { toast.error(apiError(e, t)); }
  };
  const resetPw = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPw(data.temp_password);
    } catch (e) { toast.error(apiError(e, t)); }
  };

  if (!teachers) return <SkeletonPanel lines={4} />;
  return (
    <div className="space-y-4">
      <button data-testid="add-teacher-btn" onClick={() => setFormOpen(true)} className="btn-magic text-sm"><Plus className="w-4 h-4" /> {t("admin.add_teacher")}</button>
      <button data-testid="toggle-disabled-teachers" onClick={() => setShowDisabled(!showDisabled)}
        className="text-xs font-extrabold text-[#A5A0D6] hover:text-white transition-colors px-3 py-1.5 rounded-full border border-white/15">
        {showDisabled ? t("teacher.disabled_tag") + " ✓" : t("teacher.disabled_tag")}
      </button>
      {tempPw && (
        <div data-testid="teacher-temp-password" className="rounded-2xl bg-[#FFC83D]/12 border border-[#FFC83D]/40 p-4 text-center" dir="ltr">
          <p className="text-xs font-bold text-[#A5A0D6]">{t("teacher.temp_password")}</p>
          <p className="text-2xl font-black text-[#FFC83D]">{tempPw}</p>
        </div>
      )}
      <div className="grid gap-3">
        {visibleTeachers.map((u) => (
          <div key={u.id} data-testid={`teacher-row-${u.username}`} className={`magic-panel p-4 flex items-center gap-4 flex-wrap ${!u.is_active ? "opacity-55" : ""}`}>
            <Avatar src={u.avatar} name={pick(u, "name")} size="sm" />
            <div className="flex-1 min-w-[140px]">
              <p className="font-extrabold">{pick(u, "name")}</p>
              <p className="text-xs text-[#A5A0D6] font-bold" dir="ltr">@{u.username}</p>
            </div>
            <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-[#27C9EE]/12 border border-[#27C9EE]/40 text-[#27C9EE]">
              {u.class_count} {t("admin.classes")} · {u.student_count} {t("admin.students")}
            </span>
            <button data-testid={`teacher-reset-pw-${u.username}`} onClick={() => resetPw(u)} className="text-xs font-extrabold text-[#27C9EE] hover:text-white transition-colors">{t("admin.reset_password")}</button>
            <button data-testid={`teacher-toggle-${u.username}`} onClick={() => toggleActive(u)}
              className={`text-xs font-extrabold px-3 py-1.5 rounded-full border transition-colors ${u.is_active ? "text-[#FF8FB1] border-[#EF4D9A]/40 hover:bg-[#EF4D9A]/15" : "text-[#48D276] border-[#48D276]/40 hover:bg-[#48D276]/15"}`}>
              {u.is_active ? t("admin.disable") : t("admin.enable")}
            </button>
          </div>
        ))}
      </div>
      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={t("admin.add_teacher")} testId="teacher-form-modal">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_ar")} *</label>
            <input data-testid="teacher-name-ar" className="input-magic" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></div>
          <div><label className="text-sm font-extrabold block mb-1">{t("admin.name_en")}</label>
            <input data-testid="teacher-name-en" className="input-magic" dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></div>
          <div><label className="text-sm font-extrabold block mb-1">{t("teacher.username")} *</label>
            <input data-testid="teacher-username" className="input-magic" dir="ltr" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
          <div><label className="text-sm font-extrabold block mb-1">{t("teacher.password_optional")}</label>
            <input data-testid="teacher-password" className="input-magic" dir="ltr" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <button data-testid="teacher-save-btn" disabled={busy} onClick={create} className="btn-magic col-span-2">{busy ? t("common.loading") : t("common.save")}</button>
        </div>
      </Modal>
    </div>
  );
}

function AuditTab() {
  const { t, lang } = useLang();
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data.logs)); }, []);
  if (!logs) return <SkeletonPanel lines={5} />;
  if (logs.length === 0) return <EmptyState icon="ScrollText" title={t("common.empty")} testId="audit-empty" />;
  return (
    <div className="space-y-2">
      {logs.map((l) => (
        <div key={l.id} data-testid={`audit-${l.id}`} className="magic-panel px-4 py-3 flex items-center gap-3 flex-wrap text-sm">
          <span className="font-extrabold text-[#9C42F5]">{l.user_name}</span>
          <span className="font-bold">{l.action}</span>
          <span className="text-[#A5A0D6] font-semibold">{l.entity}</span>
          <span className="ms-auto text-[11px] text-[#A5A0D6] font-bold">{fmtRelative(l.created_at, lang)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const { t } = useLang();
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-6" data-testid="admin-page">
      <h2 className="text-2xl sm:text-3xl font-black text-center">{t("admin.dashboard")}</h2>
      <div className="flex flex-wrap justify-center gap-2">
        {TABS.map((tb) => (
          <button key={tb} data-testid={`admin-tab-${tb}`} onClick={() => setTab(tb)}
            className={`px-4 py-2 rounded-full text-sm font-extrabold border transition-colors ${tab === tb ? "bg-gradient-to-r from-[#7625D8] to-[#9C42F5] border-transparent glow-purple" : "border-white/15 text-[#A5A0D6] hover:text-white"}`}>
            {t(`admin.${tb}`)}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "teachers" && <TeachersTab />}
      {tab === "levels" && <LevelsTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "badges" && <BadgesTab />}
      {tab === "users" && <UsersTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}
